/**
 * StatCard — a single KPI card on the dashboard home surface
 * (pirx-3apc9). Renders a label, a value, an icon, and a hint whose
 * color follows the hint tone (low → risk-low, high → risk-high).
 *
 * Extracted from `pages/Dashboard.tsx` (pirx-ojst0.12) — the original
 * file declared StatCard inline alongside the Dashboard route component
 * and two other inline subcomponents. Dashboard.tsx stays at `pages/`
 * (the route page) per spec "Why not fold the page file"; this card
 * only renders inside the dashboard surface and lives under
 * `features/dashboard/components/`.
 */

import type { LucideIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

interface StatCardProps {
  label: string;
  value: string | number;
  hint: string;
  /** "unavailable" dims the hint into a neutral tone (data not loaded). */
  hintTone?: "low" | "high" | "unavailable";
  icon: LucideIcon;
  /** Dim the whole card so a placeholder value can't read as real data. */
  unavailable?: boolean;
}

export function StatCard({
  label,
  value,
  hint,
  hintTone = "low",
  icon: Icon,
  unavailable = false,
}: StatCardProps) {
  return (
    <Card className={`flex-1 min-w-0 rounded-[var(--r-lg)] border-[var(--line)] bg-[var(--card)] shadow-none ${unavailable ? "opacity-60" : ""}`}>
      <CardContent className="p-[18px]">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[12.5px] font-semibold text-[var(--ink-3)]">{label}</span>
          <Icon className="size-[17px] text-[var(--ink-4)]" aria-hidden="true" />
        </div>
        <div className="mt-3 font-heading text-[30px] font-semibold leading-none tracking-[-0.02em] text-[var(--ink)]">
          {value}
        </div>
        <div
          className={
            hintTone === "high"
              ? "mt-2 text-xs font-semibold text-[var(--risk-high)]"
              : hintTone === "unavailable"
                ? "mt-2 text-xs font-medium text-[var(--ink-3)]"
                : "mt-2 text-xs font-semibold text-[var(--risk-low)]"
          }
        >
          {hint}
        </div>
      </CardContent>
    </Card>
  );
}
